/**
 * AppSkeleton — Loading skeleton that matches the app layout
 * Shown immediately while modules and data load in background
 */

import { Skeleton } from '@/components/ui/skeleton';
import type { ViewType } from '@/config/constants';

interface AppSkeletonProps {
  view?: ViewType;
}

export function AppSkeleton({ view }: AppSkeletonProps) {
  // CRM Chat view skeleton - shows context bar + chat thread
  if (view === 'CHAT') {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        {/* CRM Context Bar skeleton */}
        <div className="border-b border-border bg-card px-4 py-3 shrink-0">
          <div className="flex items-center gap-4">
            {/* From (sender) dropdown skeleton */}
            <div className="flex items-center gap-2 flex-1">
              <Skeleton className="h-4 w-12" /> {/* "From:" label */}
              <Skeleton className="h-9 w-48" /> {/* Sender dropdown */}
            </div>

            {/* Arrow icon skeleton */}
            <Skeleton className="h-4 w-4" />

            {/* To (phone) dropdown skeleton */}
            <div className="flex items-center gap-2 flex-1">
              <Skeleton className="h-4 w-8" /> {/* "To:" label */}
              <Skeleton className="h-9 w-48" /> {/* Phone dropdown */}
            </div>
          </div>
        </div>

        {/* Chat thread skeleton (without header since CRM chat uses hideHeader) */}
        <div className="flex-1 min-h-0 overflow-hidden px-4 py-4">
          {/* Messages skeleton */}
          <div className="space-y-4">
            {/* Inbound message */}
            <div className="flex gap-2 justify-start">
              <Skeleton className="h-8 w-8 rounded-full shrink-0" />
              <div className="space-y-1.5 max-w-[70%]">
                <Skeleton className="h-16 w-64 rounded-2xl" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            {/* Outbound message */}
            <div className="flex gap-2 justify-end">
              <div className="space-y-1.5 max-w-[70%]">
                <Skeleton className="h-12 w-48 rounded-2xl" />
                <Skeleton className="h-3 w-16 ml-auto" />
              </div>
            </div>
            {/* Inbound message */}
            <div className="flex gap-2 justify-start">
              <Skeleton className="h-8 w-8 rounded-full shrink-0" />
              <div className="space-y-1.5 max-w-[70%]">
                <Skeleton className="h-20 w-72 rounded-2xl" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          </div>
        </div>

        {/* Input area skeleton */}
        <div className="border-t border-border bg-card/50 p-3 shrink-0">
          <div className="flex items-end gap-2">
            <Skeleton className="h-8 w-8 rounded-lg shrink-0 mb-0.5" />
            <Skeleton className="flex-1 h-9 rounded-lg" />
            <Skeleton className="h-8 w-8 rounded-lg shrink-0 mb-0.5" />
            <Skeleton className="h-9 w-16 rounded-lg shrink-0" />
          </div>
        </div>
      </div>
    );
  }

  // Default skeleton for other views (conversations, compose, etc.)
  return (
    <div className="flex h-full flex-col bg-background animate-pulse">
      {/* Conversation list skeleton */}
      <div className="flex-1 overflow-hidden p-4">
        <div className="space-y-3">
          {/* Skeleton conversation items */}
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="flex items-start gap-3 rounded-lg border border-border bg-card p-3"
            >
              {/* Avatar skeleton */}
              <div className="h-10 w-10 rounded-full bg-muted" />

              <div className="flex-1 space-y-2">
                {/* Name skeleton */}
                <div className="h-4 w-32 rounded bg-muted" />

                {/* Message preview skeleton */}
                <div className="h-3 w-full rounded bg-muted/60" />
                <div className="h-3 w-3/4 rounded bg-muted/60" />
              </div>

              {/* Timestamp skeleton */}
              <div className="h-3 w-12 rounded bg-muted/60" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
